<template>
  <div>
    <tooltip ref="tooltip" :click-outside-without="clickOutsideWithout">
      <template #trigger="{ hide }">
        <div class="relative search-input-wrapper">
          <input
            type="text"
            ref="inputSearch"
            id="search-input--free-text"
            class="search-input w-full truncate"
            placeholder="Where are you going?"
            @input="handleSearch"
            @focus="handleFocus"
            @click="handleFocus"
            @blur="handleBlur"
            @keydown.tab="hide"
            @keydown="handleKeyDown"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            tabindex="2000"
            :value="value"
          />

          <svg
            width="20"
            height="20"
            class="search-icon text-black-light-5"
            fill="currentColor"
          >
            <use xlink:href="/img/sprite.svg#ic_location"></use>
          </svg>

          <svg
            v-if="!!value"
            width="16"
            height="16"
            class="right-icon opacity-0"
            :class="{ 'opacity-100': isFocus }"
            @click="handleClearSearchAndShowSuggestions"
          >
            <use xlink:href="/img/sprite.svg#ic_close-circle"></use>
          </svg>
        </div>
      </template>

      <template>
        <template v-if="value">
          <div
            v-if="resultList.length"
            class="drop-panel"
            :style="{ maxWidth: inputWidth + 'px' }"
          >
            <ul>
              <template v-for="(hit, index) in resultList">
                <li
                  :key="hit.id"
                  @click="handleEntrySelected(hit)"
                  class="flex items-start py-3"
                  :class="{ 'suggestion-highlighted': index === selectedIndex }"
                  v-if="
                    ['destination', 'province', 'country'].includes(hit.type)
                  "
                >
                  <svg
                    width="20"
                    height="20"
                    class="flex-shrink-0 mr-3 text-black-light-5"
                    fill="currentColor"
                  >
                    <use xlink:href="/img/sprite.svg#ic_location"></use>
                  </svg>

                  <div>
                    <p class="mdsm:mt-0.5">
                      {{ hit.title }}
                    </p>
                    <p class="search-item-subtitle">
                      {{ hit.subtitle }}
                    </p>
                  </div>
                </li>
                <li
                  v-else
                  :key="hit.id"
                  :class="{ 'suggestion-highlighted': index === selectedIndex }"
                >
                  <a :href="hit.url | handleExternalURL">
                    <svg class="destination-icon">
                      <use
                        xlink:href="/img/sprite.svg#ic_resort"
                        v-if="hit.type === 'resort'"
                      />
                      <use
                        xlink:href="/img/sprite.svg#ic_boat"
                        v-else-if="hit.type === 'liveaboard'"
                      />
                      <use
                        xlink:href="/img/sprite.svg#ic_ecoventure"
                        v-else-if="hit.type === 'ecoventure'"
                      />
                      <use
                        xlink:href="/img/sprite.svg#ic_charter-yacht"
                        v-else-if="hit.type === 'yacht'"
                      />
                    </svg>
                    <div>
                      <p v-html="hit.title" />
                      <p class="search-item-subtitle">{{ hit.subtitle }}</p>
                    </div>
                  </a>
                </li>
              </template>
            </ul>
          </div>
          <div
            v-else-if="noResult"
            class="px-6 py-4 drop-panel"
            :style="{ maxWidth: inputWidth + 'px' }"
          >
            No result found in this area.<br />Either choose a different holiday
            type or alternative destination.
          </div>
        </template>
        <template v-else>
          <div
            class="drop-panel featured-searches"
            @touchstart="handleFeatureSearchesTouched"
            ref="droppanel"
          >
            <featured-search
              :search-type="searchType"
              @change="handleDestinationClicked"
              :featured-searches="featuredSearchesResultForSearchType"
              :loading="loadingFeaturedSearches"
            />
          </div>
        </template>
      </template>
    </tooltip>
  </div>
</template>

<script>
import algoliasearchV4 from "algoliasearch";
import get from "lodash/get";
import flatMap from "lodash/flatMap";

import FeaturedSearch from "../../Search/2021/FeaturedSearch";
import Tooltip from "../../core/Tooltip";
import {
  transformAlgoliaHitsToResortResult,
  transformAlgoliaHitsToLiveaboardResult,
  transformAlgoliaHitsToEcoventureResult,
  transformAlgoliaHitsToYachtResult,
} from "../../../helpers/searchHelpers";

const KEYDOWN = 40;
const KEYUP = 38;
const ENTER = 13;

export default {
  components: {
    Tooltip,
    FeaturedSearch,
  },
  props: [
    "value",
    "searchType",
    "clickOutsideWithout",
    "calculatorPanelHeight",
  ],
  data() {
    return {
      algoliaResult: null,
      selectedIndex: 0,
      isQuerying: false,
      isFocus: false,
      loadingFeaturedSearches: false,
      featuredSearchesResult: {},
    };
  },
  mounted() {
    this.loadingFeaturedSearches = true;
    $.ajax({
      method: "POST",
      url: "/",
      data: {
        action: "zuBlu/public/getFeaturedSearches",
        [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
      },
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }).then((response) => {
      this.featuredSearchesResult = JSON.parse(response);
      this.loadingFeaturedSearches = false;
    });
  },
  computed: {
    noResult() {
      return (
        this.value.length &&
        this.resultList.length === 0 &&
        this.isQuerying === false
      );
    },
    resultList() {
      if (!this.algoliaResult) {
        return [];
      }

      const searchType = this.searchType;
      if (searchType === "resort") {
        return transformAlgoliaHitsToResortResult(
          this.algoliaResult,
          searchType
        );
      } else if (searchType === "liveaboard") {
        return transformAlgoliaHitsToLiveaboardResult(
          this.algoliaResult,
          searchType
        );
      } else if (searchType === "ecoventure") {
        return transformAlgoliaHitsToEcoventureResult(
          this.algoliaResult,
          searchType
        );
      } else if (searchType === "yacht") {
        return transformAlgoliaHitsToYachtResult(
          this.algoliaResult,
          searchType
        );
      }
    },
    inputWidth() {
      return $(this.$refs.inputSearch).outerWidth();
    },
    featuredSearchesResultForSearchType() {
      if (this.searchType === "liveaboard") {
        return this.fixRedSeach(
          this.featuredSearchesResult["liveaboard"] || []
        );
      }
      return this.fixRedSeach(this.featuredSearchesResult["default"] || []);
    },
  },
  created() {
    const Algolia = algoliasearchV4(
      window.zubluAlgolia.applicationID,
      window.zubluAlgolia.publicAPIKey
    );

    this.$algolia = Algolia;
    this.$destinationIndex = Algolia.initIndex(
      window.zubluAlgolia.destinationIndex
    );
    this.$ecoventureIndex = Algolia.initIndex(
      window.zubluAlgolia.ecoventureIndex
    );
  },
  filters: {
    handleExternalURL(url) {
      if (url.startsWith("http")) {
        return url;
      } else {
        return `http://${url}`;
      }
    },
  },
  methods: {
    fixRedSeach(featuredSearches) {
      for (const area of featuredSearches) {
        if (area.code === "RS") {
          area.code = "";

          for (const country of area.children) {
            if (
              country.children.some(
                (destination) => destination.code === "RED_SHM"
              )
            ) {
              country.code = "RS";
            } else if (
              country.children.some(
                (destination) => destination.code === "SUD_SUD"
              )
            ) {
              country.code = "SD";
            } else if (
              country.children.some(
                (destination) => destination.code === "DJI_DJI"
              )
            ) {
              country.code = "DJ";
            }
          }
        }
      }

      return featuredSearches;
    },
    showSearch() {
      this.$refs.inputSearch.focus();
    },
    handleDestinationClicked(which) {
      this.$refs.tooltip.hide();
      this.$emit("input", which.locTitle);
      this.$emit("confirmed", which);
    },
    handleFocus(e) {
      this.isFocus = true;
      this.isQuerying = true;
      this.handleSearch(e);
      this.$refs.tooltip.show();
      this.$emit("focus");
    },
    handleBlur() {
      this.isFocus = false;
    },
    handleKeyDown(e) {
      if (e.which === KEYDOWN) {
        this.selectedIndex = Math.min(
          this.resultList.length - 1,
          this.selectedIndex + 1
        );
      } else if (e.which === KEYUP) {
        this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      } else if (e.which === ENTER) {
        this.handleEntrySelected(this.resultList[this.selectedIndex]);
        this.selectedIndex = 0;
      }
    },
    handleEntrySelected(entry) {
      this.$emit("input", entry.title);
      this.$emit("confirmed", entry);
      this.algoliaResult = null;
      this.$refs.tooltip.hide();
    },
    handleSearch(e) {
      const newValue = e.target.value;
      this.$emit("input", newValue);

      if (newValue.trim().length < 1) {
        return;
      }

      const searchType = this.searchType;
      const operations = {
        resort: this.searchForResorts,
        liveaboard: this.searchForLiveaboards,
        ecoventure: this.searchForEcoventures,
        yacht: this.searchForYacht,
      };
      const operation = operations[searchType];

      this.isQuerying = true;

      operation
        .call(this, newValue)
        .then((result) => {
          this.algoliaResult = result;
        })
        .catch((error) => {
          console.error(error);
          this.algoliaResult = null;
        })
        .then(() => {
          this.isQuerying = false;
        });
    },
    handleFeatureSearchesTouched() {
      this.$refs.inputSearch.blur();
    },
    handleClearSearchAndShowSuggestions() {
      this.$emit("input", "");
      this.$refs.tooltip.show();
    },
    searchForResorts(query) {
      const queries = [
        {
          indexName: window.zubluAlgolia.resortIndex,
          query: query,
          numericFilters: ["__odyssey_price > 0"],
          attributesToRetrieve: [
            "__platform_name",
            "__platform_country_title",
            "__platform_url",
          ],
          hitsPerPage: 5,
          typoTolerance: false,
          exactOnSingleWordQuery: "word",
          queryType: "prefixLast",
        },
        {
          indexName: window.zubluAlgolia.destinationIndex,
          query: query,
          attributesToRetrieve: [
            "__platform_destination_title",
            "__platform_region_title",
            "__platform_country_title",
            "__platform_weight",
            "__platform_skuKey",
          ],
          hitsPerPage: 5,
        },
      ];

      return this.$algolia.multipleQueries(queries).then((response) => {
        const aggregatedResult = {
          hits: [],
        };

        response.results.forEach((result) => {
          result.hits.forEach((hit) => {
            hit._index = result.index;
          });
        });
        aggregatedResult.hits = flatMap(response.results, (res) => res.hits);

        return aggregatedResult;
      });
    },
    searchForLiveaboards(query) {
      const queries = [
        {
          indexName: window.zubluAlgolia.liveaboardIndex,
          query: query,
          facetFilters: ["__platform_type:liveaboard"],
          attributesToRetrieve: [
            "__platform_name",
            "__platform_country_title",
            "__platform_url",
          ],
          hitsPerPage: 5,
          typoTolerance: false,
          exactOnSingleWordQuery: "word",
          queryType: "prefixLast",
        },
        {
          indexName: window.zubluAlgolia.destinationIndex,
          query: query,
          attributesToRetrieve: [
            "__platform_destination_title",
            "__platform_region_title",
            "__platform_country_title",
            "__platform_weight",
            "__platform_skuKey",
          ],
          hitsPerPage: 5,
        },
      ];

      return this.$algolia.multipleQueries(queries).then((response) => {
        const aggregatedResult = {
          hits: [],
        };

        response.results.forEach((result) => {
          result.hits.forEach((hit) => {
            hit._index = result.index;
          });
        });
        aggregatedResult.hits = flatMap(response.results, (res) => res.hits);

        return aggregatedResult;
      });
    },
    searchForEcoventures(query) {
      return this.$ecoventureIndex
        .search(query, {
          hitsPerPage: 5,
          distinct: true,
        })
        .then((response) => {
          return response;
        });
    },
    searchForYacht(query) {
      const queries = [
        {
          indexName: window.zubluAlgolia.destinationIndex,
          query: query,
          attributesToRetrieve: [
            "__platform_destination_title",
            "__platform_region_title",
            "__platform_country_title",
            "__platform_weight",
            "__platform_skuKey",
          ],
          hitsPerPage: 5,
        },
        {
          indexName: window.zubluAlgolia.yachtIndex,
          query: query,
          facetFilters: ["__platform_type:yacht"],
          attributesToRetrieve: [
            "__platform_name",
            "__platform_country_title",
            "__platform_url",
          ],
          hitsPerPage: 5,
        },
      ];

      return this.$algolia
        .multipleQueries(queries, {
          strategy: "stopIfEnoughMatches",
        })
        .then((response) => {
          const aggregatedResult = {
            hits: [],
          };

          response.results.forEach((result) => {
            result.hits.forEach((hit) => {
              hit._index = result.index;
            });
          });
          aggregatedResult.hits = flatMap(response.results, (res) => res.hits);

          return aggregatedResult;
        });
    },
  },
  watch: {
    searchType(searchType) {
      const featuredSearches = get(
        window,
        ["topNavStore", "state", "featuredSearchStats"],
        []
      );

      this.$destinationIndex
        .search(this.value, {
          restrictSearchableAttributes: ["__platform_name"],
          attributesToRetrieve: ["__platform_skuKey"],
        })
        .then((result) => {
          const firstResult = result.hits[0];

          if (firstResult) {
            const code = firstResult.__platform_skuKey;

            if (searchType === "resort") {
              const hasResort = featuredSearches.some((where) => {
                return (
                  [where.country_code, where.destination_code].includes(code) &&
                  where.has_resort
                );
              });

              if (!hasResort) {
                this.$emit("input", "");
              }
            } else if (searchType === "liveaboard") {
              const hasLiveaboard = featuredSearches.some((where) => {
                return (
                  [where.country_code, where.destination_code].includes(code) &&
                  where.has_liveaboards
                );
              });

              if (!hasLiveaboard) {
                this.$emit("input", "");
              }
            } else if (searchType === "ecoventure") {
              const hasEcoventure = featuredSearches.some((where) => {
                return (
                  [where.country_code, where.destination_code].includes(code) &&
                  where.has_ecoventures
                );
              });

              if (!hasEcoventure) {
                this.$emit("input", "");
              }
            } else if (searchType === "yacht") {
              const hasYacht = featuredSearches.some((where) => {
                return (
                  [where.country_code, where.destination_code].includes(code) &&
                  where.has_yacht
                );
              });

              if (!hasYacht) {
                this.$emit("input", "");
              }
            }
          } else {
            this.$emit("input", "");
          }
        });
    },
    isFocus() {
      setTimeout(() => {
        const droppanel = this.$refs.droppanel;
        if (droppanel && this.calculatorPanelHeight) {
          const position = droppanel.getBoundingClientRect();
          droppanel.style.maxHeight = `calc(100vh - ${position.top}px - 24px)`;
        }
      }, 500);
    },
  },
};
</script>

<style scoped>
.suggestion-highlighted {
  background-color: var(--black-light-1);
}

.drop-panel {
  min-width: 350px;
  top: calc(100% + 24px);
  z-index: 10;
  text-align: left;
}

.drop-panel ul li {
  cursor: pointer;
  width: 100%;
  margin-bottom: 0;
  padding-left: 20px;
  padding-right: 20px;
}

.drop-panel ul li a {
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  min-height: 64px;
  margin-bottom: 0;
}

.drop-panel ul li:not(.no-result):hover {
  background-color: var(--black-light-1);
}

.destination-icon {
  flex: none;
  width: 24px;
  height: 24px;
  margin-right: 12px;
}

.search-item-subtitle {
  color: var(--black-light-4);
  font-size: 14px;
}

.search-input {
  border: none;
  padding-right: 40px;
}

.search-input-wrapper .right-icon {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  cursor: pointer;
  fill: var(--black-light-5);
}

.featured-searches {
  padding: 1.5rem;
  text-align: left;
  max-height: calc(100vh - 110px);
  overflow-y: auto;
}

@media (max-width: 768px) {
  .drop-panel {
    min-width: 0;
    top: 100%;
  }

  .featured-searches {
    margin-top: 4px;
    padding: 8px 0;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  }
}
</style>
